import React, { Component, ErrorInfo, ReactNode } from 'react';
import { Alert, Button, Container } from 'react-bootstrap';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

class ErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false,
    error: null,
    errorInfo: null,
  };

  public static getDerivedStateFromError(error: Error): State {
    // Update state so the next render will show the fallback UI
    return { hasError: true, error, errorInfo: null };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // Log error to console for debugging
    console.error('ErrorBoundary caught an error:', error, errorInfo);
    
    // Update state with error details
    this.setState({
      error,
      errorInfo,
    });

    // You can also log to an error reporting service here
    // Example: logErrorToService(error, errorInfo);
  }

  private handleReload = () => {
    // Clear error state and reload
    this.setState({ hasError: false, error: null, errorInfo: null });
    window.location.reload();
  };

  private handleGoBack = () => {
    // Clear error state and go back
    this.setState({ hasError: false, error: null, errorInfo: null });
    window.history.back();
  };

  public render() {
    if (this.state.hasError) {
      // Check if custom fallback is provided
      if (this.props.fallback) {
        return this.props.fallback;
      }

      // Default error UI
      return (
        <Container className="py-5">
          <Alert variant="danger" className="shadow-sm">
            <Alert.Heading className="d-flex align-items-center">
              <i className="bi bi-exclamation-triangle-fill me-2 fs-4"></i>
              <span>Oops! Something went wrong</span>
            </Alert.Heading>
            <hr />
            <div className="mb-3">
              <p className="mb-2">
                <strong>We encountered an unexpected error.</strong> Don't worry, your data is safe.
              </p>
              <p className="mb-0 text-muted small">
                This error has been logged and our team will look into it.
              </p>
            </div>

            {/* Show error details in development */}
            {process.env.NODE_ENV === 'development' && this.state.error && (
              <div className="mt-3">
                <details className="cursor-pointer">
                  <summary className="fw-bold mb-2" style={{ cursor: 'pointer' }}>
                    Error Details (Development Mode)
                  </summary>
                  <div className="bg-light p-3 rounded border">
                    <div className="mb-2">
                      <strong>Error:</strong>
                      <pre className="mb-0 mt-1 small text-danger">
                        {this.state.error.toString()}
                      </pre>
                    </div>
                    {this.state.errorInfo && (
                      <div>
                        <strong>Component Stack:</strong>
                        <pre className="mb-0 mt-1 small" style={{ maxHeight: '200px', overflow: 'auto' }}>
                          {this.state.errorInfo.componentStack}
                        </pre>
                      </div>
                    )}
                  </div>
                </details>
              </div>
            )}

            <hr />
            <div className="d-flex gap-2 flex-wrap">
              <Button variant="primary" onClick={this.handleReload}>
                <i className="bi bi-arrow-clockwise me-2"></i>
                Reload Page
              </Button>
              <Button variant="outline-secondary" onClick={this.handleGoBack}>
                <i className="bi bi-arrow-left me-2"></i>
                Go Back
              </Button>
              <Button
                variant="outline-secondary"
                onClick={() => (window.location.href = '/')}
              >
                <i className="bi bi-house me-2"></i>
                Go to Home
              </Button>
            </div>
          </Alert>
        </Container>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
